// client/src/components/pages/LexiStar/ReviewQueue/EvidenceMap.jsx
//
// Review-queue evidence mini-map (LS-09 §5.18, §5.7 "mini-map ± 5 min track")
// on the shared LexiStarMap: the positions five minutes either side of the
// record as a thin line with a dot per fix (hollow when the fix is weak), the
// record's own point as a ringed marker, and the geofence involved (when the
// evidence names one) as an outlined polygon. Everything drawn is also listed
// in the panel's text (LS-09 §6), so the map is supporting, not required.

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Source, Layer, Marker } from 'react-map-gl/maplibre';
import LexiStarMap from '../../../map/LexiStarMap';
import { boundsFromLngLats } from '../../../map/geo';

const DEFAULT_VIEW = { longitude: 28.5, latitude: -26.2, zoom: 6 };
const TRACK = '#334155';
const FENCE = '#0f172a';

const lonOf = (p) => p?.lon ?? p?.lng ?? p?.longitude ?? null;
const latOf = (p) => p?.lat ?? p?.latitude ?? null;
const tsOf = (p) => p?.ts ?? p?.ts_device ?? p?.at ?? p?.t ?? null;

/** Positions → sorted [{lon, lat, ts, weak}] with coordinates. */
export function evidencePoints(positions) {
  return (positions || [])
    .filter((p) => p && lonOf(p) != null && latOf(p) != null)
    .map((p) => ({
      lon: Number(lonOf(p)),
      lat: Number(latOf(p)),
      ts: tsOf(p),
      weak: (p.hdop != null && Number(p.hdop) > 5) || (p.sats != null && Number(p.sats) < 5),
    }))
    .filter((p) => Number.isFinite(p.lon) && Number.isFinite(p.lat))
    .sort((a, b) => new Date(a.ts) - new Date(b.ts));
}

const ringCoords = (geometry) => {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') return geometry.coordinates.flat();
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat(2);
  return [];
};

export default function EvidenceMap({ positions, subject, geofence, className }) {
  const mapRef = useRef(null);
  const points = useMemo(() => evidencePoints(positions), [positions]);
  const subjectPoint = useMemo(
    () =>
      subject &&
      lonOf(subject) != null &&
      latOf(subject) != null &&
      Number.isFinite(Number(lonOf(subject))) &&
      Number.isFinite(Number(latOf(subject)))
        ? { lon: Number(lonOf(subject)), lat: Number(latOf(subject)) }
        : null,
    [subject]
  );
  const fenceGeometry = geofence?.geometry || (geofence?.type === 'Polygon' ? geofence : null);

  const trackFC = useMemo(
    () => ({
      type: 'FeatureCollection',
      features:
        points.length >= 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: points.map((p) => [p.lon, p.lat]) },
              },
            ]
          : [],
    }),
    [points]
  );
  const fixFC = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: points.map((p) => ({
        type: 'Feature',
        properties: { weak: p.weak },
        geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
      })),
    }),
    [points]
  );
  const fenceFC = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: fenceGeometry ? [{ type: 'Feature', properties: {}, geometry: fenceGeometry }] : [],
    }),
    [fenceGeometry]
  );

  const bounds = useMemo(
    () =>
      boundsFromLngLats([
        ...points.map((p) => [p.lon, p.lat]),
        ...(subjectPoint ? [[subjectPoint.lon, subjectPoint.lat]] : []),
        ...ringCoords(fenceGeometry),
      ]),
    [points, subjectPoint, fenceGeometry]
  );

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !bounds) return;
    try {
      map.fitBounds(bounds, { padding: 40, duration: 0, maxZoom: 17 });
    } catch {
      /* not ready */
    }
  }, [bounds]);
  useEffect(() => {
    fit();
  }, [fit]);

  if (!points.length && !subjectPoint) {
    return (
      <p className="text-sm text-slate-500 px-3 py-6 text-center bg-slate-50 rounded-lg border border-dashed border-slate-300">
        No positions in the five minutes either side of this record.
      </p>
    );
  }

  return (
    <div>
      <div
        className={
          className ||
          'relative z-0 h-[240px] sm:h-[280px] rounded-lg overflow-hidden border border-slate-200'
        }
      >
        <LexiStarMap
          className="absolute inset-0"
          initialViewState={
            subjectPoint
              ? { longitude: subjectPoint.lon, latitude: subjectPoint.lat, zoom: 15 }
              : DEFAULT_VIEW
          }
          defaultBasemap="satellite"
          navigationPosition="top-left"
          onLoad={(e) => {
            mapRef.current = e.target;
            fit();
          }}
        >
          {fenceFC.features.length > 0 && (
            <Source id="review-fence" type="geojson" data={fenceFC}>
              <Layer
                id="review-fence-fill"
                type="fill"
                paint={{ 'fill-color': '#ffffff', 'fill-opacity': 0.12 }}
              />
              <Layer
                id="review-fence-line"
                type="line"
                paint={{ 'line-color': FENCE, 'line-width': 2, 'line-dasharray': [3, 2] }}
              />
            </Source>
          )}
          {trackFC.features.length > 0 && (
            <Source id="review-track" type="geojson" data={trackFC}>
              <Layer
                id="review-track-line"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': TRACK, 'line-width': 2.5 }}
              />
            </Source>
          )}
          {fixFC.features.length > 0 && (
            <Source id="review-fixes" type="geojson" data={fixFC}>
              <Layer
                id="review-fixes-circle"
                type="circle"
                paint={{
                  'circle-radius': 3.5,
                  'circle-color': ['case', ['get', 'weak'], '#ffffff', TRACK],
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 1,
                }}
              />
            </Source>
          )}
          {subjectPoint && (
            <Marker longitude={subjectPoint.lon} latitude={subjectPoint.lat}>
              <div
                title="The record under review"
                // literal colours: map markers do not follow the app theme
                className="w-5 h-5 rounded-full shadow-[0_0_0_2px_rgba(15,23,42,0.6)]"
                style={{ background: FENCE, border: '3px solid #ffffff' }}
              />
            </Marker>
          )}
        </LexiStarMap>
      </div>
      <ul
        className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-500"
        aria-label="Evidence map legend"
      >
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-0.5" style={{ background: TRACK }} /> Track ± 5 min (
          {points.length} fixes)
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full border border-slate-500 bg-white" />{' '}
          Weak fix
        </li>
        {subjectPoint && (
          <li className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-full ring-1 ring-slate-500"
              style={{ background: FENCE, border: '2px solid #ffffff' }}
            />{' '}
            This record
          </li>
        )}
        {fenceFC.features.length > 0 && (
          <li className="inline-flex items-center gap-1.5">
            <span className="inline-block w-4 h-0" style={{ borderTop: `2px dashed ${FENCE}` }} />{' '}
            Zone {geofence?.name ? `“${geofence.name}”` : ''}
          </li>
        )}
      </ul>
    </div>
  );
}
